import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "./Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AffiliateSignupDialog from "@/components/marketing/AffiliateSignupDialog";
import { useAuth } from "@/hooks/useAuth";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const { isAuthed } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // "Login" = existing Deriv user → VTS Desk + API token connect (3% markup)
  // "Start Trading" = new user → affiliate signup dialog (affiliate commission)
  const handleLogin = () => navigate("/trade?connect=1");
  const handleSignup = () => setSignupOpen(true);

  const links = [
    { label: "Markets", href: "#markets" },
    { label: "Platforms", href: "#platforms" },
    { label: "How it works", href: "#how" },
    { label: "FAQ", href: "#faq" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-vts-line"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-vts-silver/80 hover:text-white transition font-medium"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          {isAuthed ? (
            <Button
              data-testid="navbar-open-trade-btn"
              onClick={() => navigate("/trade")}
              className="bg-vts-blue hover:bg-vts-blueHover text-white rounded-md h-9 px-4 font-medium"
            >
              Open Trade Desk <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <>
              <Button
                data-testid="navbar-login-btn"
                onClick={handleLogin}
                variant="outline"
                className="border-vts-line bg-transparent text-white hover:bg-vts-surface hover:border-vts-blue rounded-md h-9 px-4 font-medium"
              >
                Open VTS Desk
              </Button>
              <Button
                data-testid="navbar-signup-btn"
                onClick={handleSignup}
                className="bg-vts-blue hover:bg-vts-blueHover text-white rounded-md h-9 px-4 font-medium"
              >
                Start Trading
              </Button>
            </>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-1">
          <LanguageSwitcher compact />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="text-white p-2"
            aria-label="menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-vts-line bg-black/95 backdrop-blur-xl">
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-vts-silver hover:text-white py-2 font-medium"
              >
                {l.label}
              </a>
            ))}
            <Button
              data-testid="mobile-login-btn"
              onClick={handleSignup}
              className="bg-vts-blue hover:bg-vts-blueHover text-white w-full"
            >
              Start Trading
            </Button>
          </div>
        </div>
      )}
      <AffiliateSignupDialog open={signupOpen} onOpenChange={setSignupOpen} />
    </header>
  );
};

export default Navbar;
